/**
 * Promo section behaviour (P7) — everything live ran for sections/promo.liquid,
 * as one deferred file. Live shipped the first two pieces minified inside its
 * global JS bundle and the last two as inline scripts in the section; the
 * behaviour is kept 1:1:
 *
 *  1. <promo-products-slider> — Swiper on desktop and/or mobile per the
 *     data-desktop-slider / data-mobile-layout attributes, a plain 3-column
 *     grid fallback otherwise (and while Swiper has not loaded).
 *  2. [data-bundle-trigger] / [data-promo-atc] — the drawer-CTA relay into the
 *     hidden bundle system, and the one-item async add-to-cart that renders
 *     the cart drawer/notification sections.
 *  3. [data-promo-bundle-popup] — opens the popup dialog, remaps the parent
 *     variant through the bundle configuration's variants_map and resets the
 *     selection slots.
 *  4. The discount-percentage price rewrite, gated on the section's
 *     data-discount-percentage attribute (live gated the inline script on the
 *     same setting and inlined the number).
 *
 * Renames against live, forced by the naming gate — none behavioral:
 *  - the Swiper ready queue is window.whenSwiperReady (snippets/swiper-loader.liquid);
 *  - the post-add CustomEvent is 'cart:updated' (live's spelling carried the
 *    legacy prefix; nothing in this build subscribes to the old name);
 *  - the slot-row selector is .product-bundle__product only (live paired it
 *    with a legacy-prefixed alias class that no longer exists);
 *  - the remapped bundle price key is configuration.parent_price (the rename
 *    documented in snippets/product-bundle-config.liquid).
 *
 * Dependencies: routes + fetchConfig-style globals from assets/global.js are
 * optional here — live fell back to Shopify.routes / '/cart/add.js' and that
 * fallback chain is kept.
 */
(() => {
  if (window.promoProductsInitialized) return;
  window.promoProductsInitialized = true;

  /* ------------------------------------------------------------------ *
   * 1. <promo-products-slider>
   * ------------------------------------------------------------------ */
  class PromoProductsSlider extends HTMLElement {
    connectedCallback() {
      this._onReady = () => this._init();
      if (document.readyState === 'complete') {
        this._init();
      } else {
        document.addEventListener('DOMContentLoaded', this._onReady);
      }
      this._onResize = this._handleResize.bind(this);
      window.addEventListener('resize', this._onResize);
    }

    disconnectedCallback() {
      document.removeEventListener('DOMContentLoaded', this._onReady);
      window.removeEventListener('resize', this._onResize);
      this._destroy();
    }

    _init() {
      this._currentMode = null;
      this._initForSize();
    }

    _handleResize() {
      const mode = window.innerWidth <= 768 ? 'mobile' : 'desktop';
      if (mode !== this._currentMode) {
        this._destroy();
        this._initForSize();
      }
    }

    _initForSize() {
      const isMobile = window.innerWidth <= 768;
      this._currentMode = isMobile ? 'mobile' : 'desktop';
      const desktopLayout = this.dataset.desktopSlider || 'slider';
      const mobileLayout = this.dataset.mobileLayout || 'slider';
      const wantsSlider = isMobile ? mobileLayout === 'slider' : desktopLayout === 'slider';
      const swiperElement = this.querySelector('.swiper');
      if (!swiperElement) return;

      const nav = this.querySelector('.promo-products__slider-nav');
      if (!wantsSlider) {
        swiperElement.classList.add('promo-products__grid-fallback');
        if (nav) nav.style.display = 'none';
        return;
      }
      swiperElement.classList.remove('promo-products__grid-fallback');
      if (nav) nav.style.display = '';

      if (typeof Swiper !== 'undefined') {
        this.swiper = new Swiper(swiperElement, {
          slidesPerView: isMobile ? 'auto' : 3,
          spaceBetween: isMobile ? 16 : 28,
          navigation: {
            prevEl: this.querySelector('.swiper-button-prev'),
            nextEl: this.querySelector('.swiper-button-next'),
          },
          pagination: {
            el: this.querySelector('.swiper-pagination'),
            clickable: true,
          },
          grabCursor: true,
        });
      } else if (window.whenSwiperReady) {
        window.whenSwiperReady(() => {
          if (!this.swiper && this.isConnected) this._initForSize();
        });
      }
    }

    _destroy() {
      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }
      const swiperElement = this.querySelector('.swiper');
      if (swiperElement) swiperElement.classList.remove('promo-products__grid-fallback');
    }
  }

  if (!customElements.get('promo-products-slider')) {
    customElements.define('promo-products-slider', PromoProductsSlider);
  }

  /* ------------------------------------------------------------------ *
   * 2. Drawer-CTA relay and async add-to-cart
   * ------------------------------------------------------------------ */
  function initPromoCtas() {
    document.querySelectorAll('[data-bundle-trigger]').forEach((button) => {
      if (button._bundleInit) return;
      button._bundleInit = true;
      button.addEventListener('click', (event) => {
        event.preventDefault();
        const bundleSystem = document.querySelector('.promo-products__bundle-system');
        if (!bundleSystem) return;
        const variantId = button.dataset.variantId;
        if (variantId) {
          const radio = bundleSystem.querySelector(`input[type="radio"][data-variant-id="${variantId}"]`);
          if (radio && !radio.checked) {
            radio.checked = true;
            radio.dispatchEvent(new Event('click', { bubbles: true }));
          }
        }
        const addToCart = bundleSystem.querySelector('[data-add-to-cart]');
        if (addToCart) addToCart.click();
      });
    });

    document.querySelectorAll('[data-promo-atc]').forEach((button) => {
      if (button._atcInit) return;
      button._atcInit = true;
      button.addEventListener('click', async () => {
        const variantId = button.dataset.promoAtc;
        if (!variantId) return;

        const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        const body = new FormData();
        const sectionIds =
          cart && typeof cart.getSectionsToRender === 'function'
            ? cart.getSectionsToRender().map((section) => section.id)
            : [];
        if (sectionIds.length) body.append('sections', sectionIds);
        body.append('sections_url', window.location.pathname);
        body.append('items[0][id]', variantId);
        body.append('items[0][quantity]', '1');

        button.disabled = true;
        try {
          let addUrl =
            (window.routes && window.routes.cart_add_url) ||
            (window.Shopify && window.Shopify.routes && window.Shopify.routes.root
              ? window.Shopify.routes.root + 'cart/add.js'
              : '/cart/add.js');
          if (!addUrl.endsWith('.js')) addUrl += '.js';

          const response = await fetch(addUrl, {
            method: 'POST',
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
            body,
          });
          const data = await response.json();
          if (!response.ok) throw new Error(data.description || 'Cart add failed');

          if (cart && typeof cart.renderContents === 'function' && data.sections) {
            cart.renderContents(data);
          }
          if (cart && cart.classList && cart.classList.contains('is-empty')) {
            cart.classList.remove('is-empty');
          }
          if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
            // Tracking contract (rebrush reconciliation): carry the /cart/add.js
            // response as cartData under a declared source so the datalayer's
            // add_to_cart subscriber (ADD_SOURCES) maps the added lines. The
            // bare `{ cart: data }` shape reached no subscriber.
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'promo-products', cartData: data });
          }
          document.dispatchEvent(new CustomEvent('cart:updated'));
        } catch (error) {
          console.error('Promo add to cart error:', error);
        } finally {
          button.disabled = false;
        }
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPromoCtas);
  } else {
    initPromoCtas();
  }

  /* ------------------------------------------------------------------ *
   * 3. Bundle popup opener (live's first inline script)
   * ------------------------------------------------------------------ */
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-promo-bundle-popup]');
    if (!button) return;
    const dialog = document.getElementById(`promo-bundle-popup-${button.dataset.promoBundlePopup}`);
    if (!dialog) return;

    const variantId = button.dataset.variantId;
    const bundleElement = dialog.querySelector('product-bundle');

    if (variantId && bundleElement && bundleElement.configuration && bundleElement.configuration.variants_map) {
      const variantConfiguration = bundleElement.configuration.variants_map[variantId];
      if (variantConfiguration) {
        bundleElement.configuration.id = variantConfiguration.id;
        bundleElement.configuration.sku = variantConfiguration.sku;
        bundleElement.configuration.size = variantConfiguration.size;
        bundleElement.configuration.parent_price = variantConfiguration.price;

        // Main slots only: the second selection group's slots (D-41) keep their
        // state — bundle-additional-set.js owns them and their preselection.
        const items = bundleElement.querySelectorAll(
          '[data-bundle-content] .product-bundle__product:not(.product-bundle__product--additional)'
        );
        items.forEach((item) => {
          const current = parseInt(item.getAttribute('data-current'), 10);
          if (current <= variantConfiguration.size) {
            item.classList.remove('hidden');
          } else {
            item.classList.add('hidden');
          }
          item.setAttribute('data-type', 'default');
          item.dataset.productVariantId = '';
          const label = item.querySelector('[data-selected-product]');
          if (label) label.innerText = '';
          const image = item.querySelector('img');
          if (image && bundleElement.configuration.default_image) {
            image.src = bundleElement.configuration.default_image;
          }
        });

        bundleElement.selectedProducts = [];
        bundleElement.updateAddToCartButton();
      }
    }

    document.body.classList.add('overflow-hidden');
    dialog.showModal();
    dialog.addEventListener('click', (clickEvent) => {
      if (clickEvent.target === dialog) dialog.close();
    });
    dialog.addEventListener(
      'close',
      () => {
        document.body.classList.remove('overflow-hidden');
      },
      { once: true }
    );
  });

  /* ------------------------------------------------------------------ *
   * 4. Discount-percentage price rewrite (live's second inline script)
   * ------------------------------------------------------------------ */
  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = precision == null ? 2 : precision;
    thousands = thousands == null ? ',' : thousands;
    decimal = decimal == null ? '.' : decimal;
    if (isNaN(number) || number === null) return '0';
    number = (number / 100.0).toFixed(precision);
    const parts = number.split('.');
    const dollars = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
    const cents = parts[1] ? decimal + parts[1] : '';
    return dollars + cents;
  }

  function formatMoney(cents, moneyFormat) {
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const match = moneyFormat.match(placeholderRegex);
    if (!match) return cents;
    let value = '';
    switch (match[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_space_separator':
        value = formatWithDelimiters(cents, 2, ' ', '.');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, ',', '.');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = formatWithDelimiters(cents, 0, ' ');
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
    }
    return moneyFormat.replace(placeholderRegex, value);
  }

  const checkmarkSvg =
    '<svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M10 3L4.5 8.5L2 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  function applyDiscountRewrite() {
    const moneyFormat = document.body.getAttribute('data-money-format') || '';

    document.querySelectorAll('.promo-products[data-discount-percentage]').forEach((sectionElement) => {
      const discountPct = parseInt(sectionElement.dataset.discountPercentage, 10);
      if (!discountPct || discountPct <= 0) return;

      const savingsTemplate = sectionElement.getAttribute('data-savings-template') || '[amount] saved';

      sectionElement.querySelectorAll('.promo-products__prices[data-original-price]').forEach((container) => {
        const originalCents = parseInt(container.getAttribute('data-original-price'), 10);
        if (!originalCents || originalCents <= 0) return;

        const saleElement = container.querySelector('.promo-products__price--sale');
        if (!saleElement) return;

        const existingCompare = container.querySelector('.promo-products__price--compare');
        let compareCents = originalCents;
        const currentSaleCents = originalCents;

        if (existingCompare) {
          const compareAtPrice = container.getAttribute('data-compare-at-price');
          if (compareAtPrice) {
            compareCents = parseInt(compareAtPrice, 10);
          }
        }

        const discountedCents = Math.round(currentSaleCents * (1 - discountPct / 100));

        saleElement.innerHTML = `${formatMoney(discountedCents, moneyFormat)}<span class="promo-products__price-asterisk">*</span>`;

        if (!existingCompare) {
          const compareElement = document.createElement('s');
          compareElement.className = 'promo-products__price--compare';
          compareElement.textContent = formatMoney(compareCents, moneyFormat);
          saleElement.insertAdjacentElement('afterend', compareElement);
        }

        const savingsCents = compareCents - discountedCents;
        const existingSavings = container.querySelector('.promo-products__savings');
        if (existingSavings) existingSavings.remove();

        const customText = container.getAttribute('data-savings-text');
        const savingsLabel = customText || savingsTemplate.replace('[amount]', formatMoney(savingsCents, moneyFormat));
        const showCheckmark = container.getAttribute('data-show-checkmark') === 'true';

        const savingsElement = document.createElement('span');
        savingsElement.className = 'promo-products__savings';
        savingsElement.innerHTML = `${showCheckmark ? checkmarkSvg : ''} ${savingsLabel}`;

        const compareAfter = container.querySelector('.promo-products__price--compare');
        if (compareAfter) {
          compareAfter.insertAdjacentElement('afterend', savingsElement);
        }
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', applyDiscountRewrite);
  } else {
    applyDiscountRewrite();
  }
})();
